//
//  types.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 22, 2026
//  Author: Bob Keathley
//
//  Wire shapes for the console, mirrored from the API spec section 6. The UI
//  reads the contract and never edits it. Spend fields are transcript estimates
//  and always render with an est. label.
//
// Wire shapes, mirrored from docs/api/SIGNET_API_SPECIFICATION.md §6. `ui` reads the contract; it never edits it.
/** §6i: `provisioning` (job running) -> `active` | `failed`; `retired` once offboarded. */
export type TenantStatus = 'provisioning' | 'active' | 'failed' | 'retired'
export type Tenant = { id: string; name: string; slug: string; status: TenantStatus | string; schema_version: string | null; created_at: number; db_name?: string; error?: string | null; provisioned_at?: number | null; job_execution?: string | null }
/** §6i `POST /v1/platform/tenants` body: no password -- the owner sets one from the emailed setup link. */
export type TenantCreate = { name: string; owner: { email: string; display_name: string } }
/** §6i `POST /v1/auth/setup` body. */
export type SetupRequest = { code: string; password: string }
/** SIG-66 `POST /v1/auth/forgot` body. Always answers 202 with an identical body (no account enumeration). */
export type ForgotRequest = { email: string; console: 'tenant' | 'platform' }
/** SIG-66 §9 `POST /v1/auth/password` (any role): `403 invalid_current_password` on a wrong current password. */
export type PasswordChange = { current_password: string; new_password: string }
/** SIG-66: a user row as listed by `GET /v1/users` / `GET /v1/platform/users`; `password_set` is false until the setup link is redeemed. */
export type ManagedUser = import('../auth').User & { password_set: boolean }
/** Who put a machine on the fleet. §6m: `enrolled_via` is `enroll_code` | `device_login` | null; for a code, `enrolled_by` is the admin who minted it. */
export type MachineEnrolment = { enrolled_by?: { id: string; display_name: string; email: string } | null; enrolled_via?: string | null }
export const enrolledText = (m: MachineEnrolment): string =>
  m.enrolled_via === 'device_login' ? (m.enrolled_by ? `Added by ${m.enrolled_by.display_name} at sign-in` : 'Added at sign-in')
  : m.enrolled_via === 'enroll_code' ? (m.enrolled_by ? `Added by enroll code (minted by ${m.enrolled_by.display_name})` : 'Added by enroll code')
  : '—'
export type Machine = MachineEnrolment & { id: string; tenant_id: string; hostname: string; label: string; client_build: string; created_at: number; last_seen_at: number | null; revoked_at: number | null }
export type EnrollCode = { id: string; label: string; created_by: string; created_at: number; expires_at: number; redeemed_at: number | null; redeemed_machine_id: string | null }
export type MintedCode = { code: string; expires_at: number; id: string; command: string }
/** §6m: the person whose segments the run/notebook carries (machine-user attribution); null when unknown. */
export type ActivityUser = { id: string; display_name: string; email: string }
export const userLabel = (u: ActivityUser | null | undefined): string => (u ? `${u.display_name} <${u.email}>` : '')
/** The template version a Notebook was born from (§6e/§6m); null when unknown. */
export type SourceTemplate = { namespace: string; name: string; version?: string | null }
export const templateRef = (t: SourceTemplate | null | undefined, withVersion = true): string => (t ? `${t.namespace}/${t.name}${withVersion && t.version ? `@${t.version}` : ''}` : '—')
export type Notebook = { title?: string | null; template?: SourceTemplate | null; user?: ActivityUser | null; id: string; tenant_id: string; machine_id: string | null; kind: string; name: string; genesis_head: string; attested_at: number; head_link: string; last_seq: number; last_seen_at: number | null; divergence: string | null; deny_count: number; watch_count: number }
export type Deed = { id: string; notebook_id: string; seq: number; ts: number; agent: string; tool: string; decision: string; review: boolean; reason: string | null; action: string | null; target: string | null; kind: string | null; phase: string | null; exception_id: string | null; seal: string; content_redacted?: boolean }
export type FleetSummary = { machines: Machine[]; notebooks: Notebook[]; totals: { machines: number; notebooks: number; deeds: number; deny: number; watch: number; diverged: number; runs: number; live_runs: number; usd_est: number } }
export type Page<T> = { items: T[]; next_cursor: number | null }

// -- §6e Fleet runs (SIG-42) -- a run is a live `GROUP BY session_id` across every notebook of the
// acting tenant, computed on read; nothing new is stored. `usd` (per agent) / `usd_est` (totals) are
// always a transcript-derived estimate, never a billed figure -- `ui` renders every occurrence with a
// visible "est." and the title text from the spec verbatim (see format.tsx / live.ts). `ui` reads the
// contract; it never edits docs/api/SIGNET_API_SPECIFICATION.md.
export type RunAgent = { template?: SourceTemplate | null; title?: string | null; user?: ActivityUser | null; notebook_id: string; agent: string; panes: string[]; deeds: number; deny: number; watch: number; hold: number; usd: number | null }
export type RunTotals = { deeds: number; deny: number; watch: number; hold: number; usd_est: number | null; models: string[] }
export type Run = {
  session_id: string
  workstream_id: string | null
  started_at: number
  last_at: number
  live: boolean
  env: string | null
  attended: boolean | null
  agents: RunAgent[]
  totals: RunTotals
  phases: string[]
  /** §6e source note: computed over all the run's rows. Present on run detail. */
  source?: "activity" | "legacy" | "mixed"
}

/** `GET /v1/fleet/runs/{session_id}` -- the run shape plus its interleaved timeline. */
/** §6e source note: every timeline item carries these; all `null` for legacy rows. A run whose items are all `source: "legacy"` is a legacy-chain run. */
export type ItemSource = { source?: "activity" | "legacy"; stream_id?: string | null; capture_ref?: number | null; publication_seq?: number | null }
export type RunDetail = Run & { timeline: { items: RunTimelineEntry[]; next_cursor: string | null } }

/** §6e timeline row (SIG-126 delta): every row has `type` -- `"deed"` or `"event"`. Ordered `ts desc, notebook_id desc, seq desc`.
 * A deed row keeps the shape and the redaction rule of `GET /v1/notebooks/{id}/deeds` (06 §4a). The respawn
 * marker is not a field -- `ui` derives it by diffing consecutive deed rows' `seen` within one `notebook_id`. */
export type DeedEntry = ItemSource & {
  type?: 'deed'
  notebook_id: string
  agent: string
  seen: string | null
  seq: number
  ts: number
  tool: string
  decision: string
  review: boolean
  reason: string | null
  action: string | null
  target: string | null
  kind: string | null
  phase: string | null
  exception_id: string | null
  content_redacted: boolean
  seal: string
}

/** §6q canonical event names, the `types=` vocabulary (with `deed`). Unknown host events arrive as `other`. */
export const EVENT_NAMES = [
  'user_prompt', 'tool_result', 'tool_failure', 'permission_request', 'permission_denied', 'session_start', 'session_end',
  'subagent_start', 'subagent_stop', 'stop', 'pre_compact', 'post_compact', 'notification', 'task_created', 'task_completed',
  'teammate_idle', 'agent_message', 'other',
] as const
export type EventName = (typeof EVENT_NAMES)[number]
export const TIMELINE_TYPES = ["deed", "capture", "evidence", "request", ...EVENT_NAMES] as const
export type TimelineType = (typeof TIMELINE_TYPES)[number]

/** §6q event row. `fields` is non-content metadata (`tool_name`, `duration_ms`, `to` on `agent_message`, `task_id`, …);
 * `content` is returned only to security-admin+, else `content: null, content_redacted: true`. */
export type EventEntry = ItemSource & {
  type: 'event'
  event: EventName | string
  host_event: string
  notebook_id: string
  seq: number
  ts: number
  agent: string
  agent_id: string | null
  agent_type: string | null
  prompt_id: string | null
  tool_use_id: string | null
  fields: Record<string, unknown>
  content: Record<string, unknown> | null
  content_redacted: boolean
  seal: string
}

/** §6e/§6r `capture`: metadata always; the payload is content (gated per §6q). Payload: `content: {payload_b64}` or `content: null` + `content_redacted: true`. */
export type CaptureEntry = ItemSource & { type: "capture"; notebook_id: string; seq: number; ts: number; agent?: string; host_event: string; host: string; size: number; sha256: string; content?: { payload_b64: string } | null; content_redacted?: boolean; seal?: string }

/** §6r `evidence` / `request`: non-authoritative (D121), `summary`/`body` content-gated, `authoritative` always false. */
export type EvidenceEntry = ItemSource & { type: "evidence" | "request"; notebook_id: string; seq: number; ts: number; agent?: string; kind: string; content?: { summary?: string | null; body?: Record<string, unknown> | null } | null; content_redacted?: boolean; authoritative: false; seal?: string }

export type RunTimelineEntry = (DeedEntry | EventEntry | CaptureEntry | EvidenceEntry)
export const isCapture = (e: RunTimelineEntry): e is CaptureEntry => e.type === "capture"
export const isEvidence = (e: RunTimelineEntry): e is EvidenceEntry => e.type === "evidence" || e.type === "request"
export const isEvent = (e: RunTimelineEntry): e is EventEntry => e.type === 'event'
/** The `types=` name a row filters under: `deed`, or its event name. */
export const timelineType = (e: RunTimelineEntry): string => (isEvent(e) ? e.event : isCapture(e) || isEvidence(e) ? e.type : "deed")

export type Workstream = {
  workstream_id: string
  runs: number
  live_runs: number
  /** distinct notebook names across every run in the workstream, alphabetical */
  agents: string[]
  totals: { deeds: number; deny: number; watch: number; hold: number; usd_est: number | null }
  started_at: number
  last_at: number
}

/** A page returned by the §6e `since=` list routes (`/fleet/runs`, `/fleet/workstreams`), plus
 * `server_time` so the 5s poller's next request uses the server's clock, not its own. */
export type LivePage<T> = { items: T[]; next_cursor: number | null; server_time: number }

// --- Birthing v1 (SIG-38/SIG-36) -------------------------------------------------------------
// Wire shapes for §6a (canon), §6b (mandate templates + intake/check) and §6c (notebook preview).
// Reconciled against SIGNET_API_SPECIFICATION.md 2026-09-22 (SIG-36/SIG-38 shapes commit); no
// guessed fields remain. `ui` reads the contract; it never edits it.

/** §6b `template` shape -- identical for `control.mandate_templates` and `tenant.mandate_templates`. */
export type CatalogueContentType = 'template' | 'phases' | 'hook_set'
export const CONTENT_TYPES: { id: CatalogueContentType; label: string }[] = [
  { id: 'template', label: 'Templates' },
  { id: 'phases', label: 'Phase sets' },
  { id: 'hook_set', label: 'Hook sets' },
]
export const TITLE_MAX = 80
/** Doc 37 D100: a title is one line, at most 80 characters. */
export const titleError = (t: string): string | null =>
  !t.trim() ? 'A title is required.' : /[\r\n]/.test(t) ? 'A title is one line.' : t.length > TITLE_MAX ? `A title is at most ${TITLE_MAX} characters.` : null
/** Display name: the title when present (nullable on rows that predate doc 37), else namespace/name. */
export const templateLabel = (t: { title?: string | null; namespace: string; name: string }): string => t.title || `${t.namespace}/${t.name}`

export type MandateTemplate = {
  id: string
  /** §6n: default `template`. Absent on responses from a server that predates it. */
  content_type?: CatalogueContentType
  /** Doc 37 D100/D101: one line, ≤ 80; markdown description. Null on rows that predate them. */
  title?: string | null
  description?: string | null
  namespace: string
  name: string
  version: string
  source: 'platform' | 'tenant'
  forked_from: string | null
  mandate: Record<string, unknown>
  compiled: Record<string, unknown>
  compiled_hash: string
  provenance: {
    keys: Record<string, string>
    items: Record<string, string[]>
    dropped: { key: string; kept: unknown; kept_from: string; dropped: unknown; dropped_from: string; why: string }[]
    retired_at: number | null
    /** Set when the template came from a catalogue import: `source` is the collection (LG or TRXY). Absent otherwise. */
    catalogue_import?: { source?: string; [k: string]: unknown }
  }
  created_by: string
  created_at: number
  signed_by: string | null
  signature: string | null
}

/** §6c `POST /v1/notebooks/preview` -- exactly one of `template_id` or `answers`. */
export type NotebookPreviewRequest = { template_id: string; agent?: string } | { answers: IntakeAnswers }

/** The ten intake question ids as keys, exactly as `POST /v1/mandates/intake/check` names them (§6b/§6c). */
export type IntakeAnswers = {
  agent: string
  role: string
  scopes: string[]
  never: { pattern: string; reason: string }[]
  escalate: { pattern: string; reason: string }[]
  budget: { usd_per_day?: number; tokens_per_session?: number }
  environments: string[]
  model: string
  effort: string
  contracts: string[]
}

export type PreviewRule = { key: string; level: string; file: string; version: string | null; rule: unknown; reason: string | null; immutable: boolean }
export type PreviewDropped = { key: string; kept: unknown; kept_from: string; kept_level: string; dropped: unknown; dropped_from: string; dropped_level: string; why: string }
export type PreviewDiff = { added: Record<string, unknown>; removed: Record<string, unknown>; changed: Record<string, { from: unknown; to: unknown }> } | null

export type NotebookPreview = {
  agent: string
  source: string
  levels: Record<string, string>
  rules: PreviewRule[]
  dropped: PreviewDropped[]
  sealed_matches_files: null
  template_id: string
  diff_vs_template: PreviewDiff
}

// -- §6a canon -----------------------------------------------------------------------------------
/** The four labels every canon response must carry so a placeholder is never shown as measured. */
export type CanonLabels = { k: string; tripwire: string; risk_slope: string; base: string; corpus: string }

export type CanonAxis = { id: string; name: string; family: string; description: string; low: string; high: string }
export type CanonSector = { sector: string; frame: string; scales: string[][]; base: number[]; sample: { name: string; answers: string[] }; pack: string; corpus_size: number }

export type CanonSectors = {
  axes: CanonAxis[]
  questions: string[]
  k: number
  tripwire: number
  risk_slope: number
  sectors: CanonSector[]
  labels: CanonLabels
}

export type CanonPreviewRequest = { sector: string; agent_name: string; answers: string[]; accountability?: string }

export type CanonCastMember = { id: string; role: string; distance: number }
export type CanonTripwire = { nearest: string; distance: number; cast_mean_distance: number; margin: number; tripped: boolean }
export type CanonGenesis = {
  sector: string
  agent: string
  seed_coordinate: Record<string, number>
  calibration: Record<string, string>
  weights: Record<string, number>
  canon_cast: CanonCastMember[]
  dominant_range: string
  adversarial_tripwire: CanonTripwire
  accountability_anchor: string
  corpus_size: number
  k: number
  pack: string
  matching: string
}
export type CanonRecord = { rn_id: string; born_utc: string; genesis: CanonGenesis; genesis_seal: string; status: 'identity-bearing' | 'QUARANTINE-REVIEW' }
export type CanonProjectionPoint = { id: string; role: string; xy: [number, number]; cast: boolean; adversarial_nearest: boolean }

export type CanonPreview = {
  record: CanonRecord
  axes: { id: string; name: string; answer: string; r: number; base: number; factor: number; weight: number }[]
  seed: number[]
  weights: number[]
  cast_ids: string[]
  adversarial_nearest_id: string
  projection: { points: CanonProjectionPoint[]; seed: [number, number] }
  k: number
  tripwire: number
  risk_slope: number
  labels: CanonLabels
}

/** `POST /v1/canon/births` (admin) / `GET /v1/canon/births[/{id}]` -- one immutable stored canon
 * identity record. Same maths and shape as `CanonPreview`, plus the storage envelope. */
export type CanonBirth = {
  id: string
  created_by: string
  created_at: number
  sector: string
  agent_name: string
  record: CanonRecord
  axes: CanonPreview['axes']
  seed: number[]
  weights: number[]
  cast_ids: string[]
  adversarial_nearest_id: string
  projection: CanonPreview['projection']
  k: number
  tripwire: number
  risk_slope: number
  labels: CanonLabels
}

export type CanonBirthRequest = { sector: string; agent_name: string; answers: string[]; accountability?: string }

/** §6a `409 canon_birth_exists` -- `rn_id` is deterministic in (sector, agent_name, answers,
 * accountability) and unique per tenant; a repeated identical birth never overwrites. */
export type CanonBirthExistsDetails = { rn_id: string; existing_id: string }

// -- §6b `POST /v1/mandates/intake/check` -- the server-truth authority ceiling ------------------
export type IntakeCeilingDetails = { question_id: string; role: string; ceiling: unknown; requested: unknown; over: unknown }

// -- §6d birth certificates (SIG-38 Step 4) -- the cross-lane contract with Warp's SIG-39 --------
// "Latest at redeem" (B3): mint signs nothing. `ui` reads the contract; it never edits it.

/** `POST /v1/notebooks/birth-cert` body -- exactly one of `template_id`/`answers` (same discriminant
 * as preview, §6c), plus birth-specific fields. `canon_birth_id` and inline `canon` are mutually
 * exclusive. */
export type BirthCertMintRequest = {
  template_id?: string
  answers?: IntakeAnswers
  agent: string
  kind: string
  team?: string
  canon_birth_id?: string
  canon?: { sector: string; answers: string[] }
  note?: string
  machine_binding?: string
}

/** `201` mint response -- the code is shown exactly once, here. Nothing is signed yet (B3). */
export type BirthCertMint = { request_id: string; code: string; expires_at: number; command: string; resolves: 'latest-at-redeem' }

/** `GET /v1/tenants/me/keys/birth` -- the tenant's Ed25519 public key, PEM-armored. No private material. */
export type BirthKey = { key_id: string; alg: string; public_key_pem: string }

export type BirthCertStatus = 'pending' | 'redeemed' | 'expired' | 'refused' | 'born'

/** `GET /v1/notebooks/birth-certs` / `/{id}` -- merges request + cert state for `#/births`.
 * `cert` is `null` while `status` is pending/expired/refused -- nothing was ever signed. */
export type BirthCertSummary = {
  id: string
  /** Title of the template version the request resolves to; null for answers-sourced or untitled. Read-only; mint takes none. */
  title?: string | null
  agent: string
  kind: string
  team: string | null
  status: BirthCertStatus
  source: 'template' | 'answers' | 'import'
  template: { namespace: string; name: string; version?: string | null } | null
  canon_status: 'identity-bearing' | 'QUARANTINE-REVIEW' | null
  note: string | null
  issued_by: string
  issuer_authority: string
  issued_at: number
  expires_at: number
  cert: {
    template_version: string | null
    resolved_at: number | null
    born_in_console_at?: number | null
    redeemed_by_machine: string | null
    redeemed_at: number | null
    attested_notebook_id: string | null
    attested_at: number | null
    key_id: string | null
  } | null
  /** `null` unless `status` is `refused` or a touched, terminally-refused `expired` (read from the
   * latest `birth.redeem_refused` audit row, not a stored column). */
  refused_reason: 'code_used' | 'code_expired' | 'retired' | 'ceiling_exceeded_at_redeem' | 'issuer_inactive' | 'invalid_compiled_mandate' | null
  /** §6d: the server-issued Notebook ID (uuid); additive, absent on older servers. */
  published_notebook_id?: string | null
}

/** `GET /v1/notifications/token` (§6j, SIG-72). The token is held in memory only. */
export type NotificationToken = {
  user_id: string
  token: string
  /** Unix seconds. */
  expires_at: number
}

// -- §6l (SIG-74) credential-email delivery status. `GET /v1/users/{id}/mail`,
// `GET /v1/platform/users/{id}/mail`, `GET /v1/platform/tenants/{tid}/users/{uid}/mail`.
// Latest per workflow, newest first; empty when nothing was ever sent. Status only, never content.
export type MailStatus = 'queued' | 'sent' | 'delivered' | 'bounced' | 'undelivered' | 'unknown'
export type MailItem = { workflow: string; sent_at: number; status: MailStatus; status_at: number | null }
// §6l polling: fetch on load and after a resend, then every 10 s for 60 s. Never indefinitely.
export const MAIL_POLL_MS = 10_000
export const MAIL_POLL_WINDOW_MS = 60_000
export const isBadMail = (s: MailStatus) => s === 'bounced' || s === 'undelivered'
/** The item that decides the chip: any failed workflow wins (a bounce is never hidden), else the newest. */
export function pickMail(items: MailItem[]): MailItem | null {
  if (!items.length) return null
  return items.find((i) => isBadMail(i.status)) ?? [...items].sort((a, b) => b.sent_at - a.sent_at)[0]
}
const mailStamp = (s: number | null | undefined) => (s ? new Date(s * 1000).toLocaleString() : 'time unknown')
export function mailTitle(i: MailItem | null): string {
  if (!i) return 'No sign-in email recorded for this user.'
  return `${i.workflow} · sent ${mailStamp(i.sent_at)} · ${i.status}${i.status_at ? ` as of ${mailStamp(i.status_at)}` : ''}`
}

// -- §6m device sessions & machine users (RB-16 K1) ----------------------------------------------
/** `GET /v1/users/me/devices` · `/v1/users/{id}/devices` item. Never a token or hash. Epoch seconds. */
export type DeviceSession = {
  id: string
  family_id: string
  machine_id: string
  os_user: string
  device_label: string
  created_at: number
  last_used_at: number | null
  access_expires_at: number
  refresh_expires_at: number
  revoked_at: number | null
  revoke_reason: 'self' | 'admin' | 'password_change' | 'machine_user_removed' | 'refresh_reuse' | null
}
/** `GET /v1/machines/{id}/users` item (doc 28 §1 triple: tenant, machine, user). */
export type MachineUser = {
  id: string
  machine_id: string
  user_id: string
  user: { display_name: string; email: string }
  os_user: string
  user_public_key: string
  created_at: number
  removed_at: number | null
}
/** Open with Bob: admin device-revoke authority (spec says team-admin+). One constant so it is a one-line change. */
export const DEVICE_REVOKE_MIN_ROLE = 'team-admin' as const

// -- §6n policy releases -------------------------------------------------------------------------
/** `GET /v1/policy/releases` item: metadata only. `hash` is the SHA-256 of the release bytes. */
/** One notebook entry inside a decoded release (§6n), for display only. */
export type ReleaseNotebook = { agent: string; notebook_id: string | null; template_id: string; template_name: string; template_version: string; title: string | null }
export type PolicyReleaseMeta = { id: string; seq: number; release_id: string; issued_by: string; issued_at: number; key_id: string; notebook_count: number; hash: string }
// -- §6p template assignments (W1.5, SIG-109): the platform gives templates to tenants ------------
/** `GET/POST /v1/platform/catalogue/{id}/tenants` item. */
export type TemplateAssignment = { tenant_id: string; slug: string; name: string; assigned_by: string; assigned_at: number }
/** `GET /v1/platform/tenants/{id}/templates` item: the §6b template plus who assigned it and when. */
export type AssignedTemplate = MandateTemplate & { assigned_at: number; assigned_by: string }
/** `POST /v1/notebooks/birth` body: the mint body minus `machine_binding` (a console birth has no machine). */
export type ConsoleBirthRequest = Omit<BirthCertMintRequest, 'machine_binding'>
/** `GET /v1/policy/adoptions` item. */
export type PolicyAdoption = { id: string; template_id: string; template_source: 'platform' | 'tenant'; version: string; applies_to: unknown; adopted_by: string; adopted_at: number }
